import { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

/**
 * The one heading style every landing section uses.
 *
 * Each section used to size its own: `text-2xl` in Orbitron for PICK A SIDE, `font-monument
 * text-xl` for WELCOME TO BR1: INFINITE, `max(var(--text-sm),2cqi)` in three more. On a phone that
 * put four section headings at four different sizes in two different typefaces, and the widest of
 * them wrapped onto a second line.
 *
 * Sized in `vw`, not `cqi`. The old container units were unreliable here — two of the sections
 * declare `contain-inline-size` / `container-inline-size`, and neither is a class this Tailwind
 * build emits (the plugin in `tailwind.config.js` provides `container-inline`), so those `cqi`
 * values were silently falling back to the viewport anyway. The ones that did have a container each
 * had a *different* container: Partners is 65% of the page, News is 75%, a video slide is full
 * bleed. Same heading, three sizes. The viewport is the thing all five sections actually share.
 *
 * `min()` with no floor, and the coefficient is not arbitrary. Monument Extended Ultrabold is a very
 * wide face — measured off `MonumentExtended-Ultrabold.otf`, the longest heading on the page
 * ("SPAWN, KILL, EARN, REPEAT") is 18.64em, so it needs a viewport 18.64 times its own font size
 * plus the gutter. At 4.6vw that holds down to 320px with the `px-3` gutter and ~8% to spare for a
 * desktop scrollbar, which `vw` counts and the layout does not. A `clamp()` floor would buy a
 * fraction of a pixel at the small end and break the one guarantee this component exists to make.
 *
 * `h2` because the page's `h1` is the hero line in `Logo.tsx`. Four of these were `h1` as well.
 */
export const SectionHeading = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
    <h2
        className={twMerge(
            'px-3 text-center font-monument font-bold uppercase leading-tight text-[min(4.6vw,2.5rem)]',
            className
        )}
    >
        {children}
    </h2>
)

/**
 * The line under a section heading.
 *
 * Deliberately not tied to the heading's scale. A subheading is a sentence and is allowed to wrap,
 * so it has no reason to shrink with the widest heading on the page — pinned to the heading's ramp
 * it would compute to 11px on a phone. It gets a reading floor instead.
 */
export const SectionSubheading = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
    <p
        className={twMerge(
            'px-4 text-center font-bold leading-relaxed text-white/70 text-[clamp(0.8125rem,1.5vw,1.125rem)]',
            className
        )}
    >
        {children}
    </p>
)
